import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { AtivacaoSectionCardComponent } from '@shared/components/ativacao-section-card/ativacao-section-card.component';
import { MaskService } from '@shared/services/mask.service';
import { AlertService, AlertType } from '@shared/services/alert.service';

/**
 * Seção "ISSQN — Data de competência" da aba Totais. O cartão de ativação
 * expande o campo de data, que usa mat-datepicker com máscara DD/MM/AAAA e as
 * mesmas validações dos campos de vigência.
 */
@Component({
    selector: 'app-totais-issqn-competencia-section',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatDatepickerModule,
        AtivacaoSectionCardComponent,
    ],
    templateUrl: './issqn-competencia-section.component.html',
    styleUrl: './issqn-competencia-section.component.css',
})
export class IssqnCompetenciaSectionComponent {
    @Input({ required: true }) form!: FormGroup;

    constructor(private alertService: AlertService) {}

    public get overrideControl(): FormControl {
        return this.form.get('_issqnOverrideAtivo') as FormControl;
    }

    public get dataControl(): FormControl {
        return this.form.get('dataCompetenciaIssqn') as FormControl;
    }

    /** Ao desativar o override, limpa a data de competência. */
    public onOverrideChange(ativo: boolean): void {
        if (!ativo) {
            this.dataControl.setValue(null);
        }
    }

    /** Máscara DD/MM/AAAA: formata e atualiza o FormControl com o Date ao completar. */
    public onDateInput(event: Event): void {
        MaskService.onDateInput(event, this.dataControl);
    }

    /** Apenas dígitos e teclas de navegação. */
    public onDateKeydown(event: KeyboardEvent): void {
        const teclasPermitidas = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'];
        const isAtalho = (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
        if (teclasPermitidas.includes(event.key) || isAtalho) return;
        if (!/^\d$/.test(event.key)) {
            event.preventDefault();
        }
    }

    /** Valida o texto digitado no blur; avisa e limpa quando inválido. */
    public onDateBlur(event: FocusEvent): void {
        const input = event.target as HTMLInputElement;
        const valor = input?.value?.trim() || '';
        if (!valor) {
            this.dataControl.setValue(null);
            this.dataControl.markAsTouched();
            return;
        }

        const apenasNumeros = valor.replace(/\D/g, '').substring(0, 8);
        const data = apenasNumeros.length === 8 ? this.parseDataBr(apenasNumeros) : null;
        if (!data) {
            input.value = '';
            this.dataControl.setValue(null);
            this.dataControl.markAsTouched();
            this.alertService.show('Atenção', 'Informe uma data válida no formato DD/MM/AAAA.', AlertType.warning);
            return;
        }

        this.dataControl.setValue(data);
        this.dataControl.markAsTouched();
    }

    private parseDataBr(apenasNumeros: string): Date | null {
        const dia = Number(apenasNumeros.substring(0, 2));
        const mes = Number(apenasNumeros.substring(2, 4));
        const ano = Number(apenasNumeros.substring(4, 8));
        if (ano < 1000 || ano > 9999 || mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
        const data = new Date(ano, mes - 1, dia);
        data.setHours(0, 0, 0, 0);
        if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) return null;
        return data;
    }
}
